import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './App.css';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Inventory from './pages/Inventory';
import Customers from './pages/Customers';
import Orders from './pages/Orders';
import Reports from './pages/Reports';
import Users from './pages/Users';
import Login from './pages/Login';
import { clearAuth, getStoredUser, getToken, initAuthHeader } from './utils/auth';

function App() {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [currentUser, setCurrentUser] = useState(getStoredUser());
  const [authLoading, setAuthLoading] = useState(Boolean(getToken()));
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth : 1280
  ));

  useEffect(() => {
    initAuthHeader();

    const verifySession = async () => {
      if (!getToken()) {
        setAuthLoading(false);
        return;
      }

      try {
        const response = await axios.get('/api/auth/me');
        setCurrentUser(response.data.user);
      } catch (error) {
        clearAuth();
        setCurrentUser(null);
      } finally {
        setAuthLoading(false);
      }
    };

    verifySession();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = useMemo(() => viewportWidth <= 768, [viewportWidth]);

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    closeSidebar();
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      clearAuth();
      setCurrentUser(null);
      setCurrentPage('dashboard');
      closeSidebar();
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard />;
      case 'products':
        return <Products />;
      case 'inventory':
        return <Inventory />;
      case 'customers':
        return <Customers />;
      case 'orders':
        return <Orders />;
      case 'reports':
        return <Reports />;
      case 'users':
        return <Users currentUser={currentUser} />;
      default:
        return <Dashboard />;
    }
  };

  if (authLoading) {
    return <div className="loading">Đang kiểm tra đăng nhập...</div>;
  }

  if (!currentUser) {
    return <Login onLogin={setCurrentUser} />;
  }

  return (
    <div className={`app ${isMobile ? 'app-mobile' : 'app-desktop'}`}>
      <Sidebar
        currentPage={currentPage}
        setCurrentPage={handlePageChange}
        currentUser={currentUser}
        onLogout={handleLogout}
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
        isMobile={isMobile}
      />
      <main className="main-content">
        <header className="app-header">
          <div className="app-header-row">
            <div className="app-header-title-group">
              {isMobile && (
                <button
                  type="button"
                  className="sidebar-toggle"
                  onClick={() => setIsSidebarOpen(true)}
                  aria-label="Mở menu điều hướng"
                >
                  ☰
                </button>
              )}
              <div className="app-header-text">
                <h1>Hệ thống Quản Lý Bán Hàng</h1>
                <div className="app-header-subtitle">
                  Xin chào, {currentUser.full_name} ({currentUser.role === 'admin' ? 'Quản trị' : 'Nhân viên'})
                </div>
              </div>
            </div>
          </div>
        </header>
        <div className="page-content">
          {renderPage()}
        </div>
      </main>
    </div>
  );
}

export default App;
